import {
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ImageIcon from "@material-ui/icons/Image";
import { EContractStatus } from "api/enums";
import clsx from "clsx";
import Chip from "components/UI/Chip";
import { lang } from "i18nLang";
import PropTypes from "prop-types";
import { useRef } from "react";
import { useHistory } from "react-router-dom";
import { ContractListStore, ContractStore, MessageStore } from "store";
import defaultCover from "../../assets/images/card_cover.png";
import { formatNumberToMonetary } from "../../utils";
import ContractCardProgress from "./ContractCardProgress";
import ContractShareButton from "./ContractShareButton";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(4, 0),
    cursor: "pointer",
    "& .action-button": {
      display: "none",
    },
    "&:hover .action-button": {
      display: "unset",
    },
  },
  cardButton: {
    float: "right",
    color: "#FFFFFF",
    margin: "-12px -12px 0 0",
  },
  cardTitle: {
    fontSize: "0.875rem",
    lineHeight: "1rem",
    fontWeight: 700,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardText: {
    fontSize: "0.8rem",
    lineHeight: "1rem",
    fontWeight: 300,
    paddingTop: theme.spacing(1),
  },
  rightText: {
    fontSize: "0.6rem",
    fontWeight: 500,
    float: "right",
  },
  media: {
    height: 80,
    boxShadow: "inset 0 0 0 10000px rgba(0, 0, 0, 0.5)",
    padding: theme.spacing(4),
  },
  chip: {
    marginBottom: "0.5rem",
  },
}));

type IContractCardProps = {
  id: string;
  project: string;
  name: string;
  client: string;
  location: string;
  value: number;
  measuredValue: number;
  status: string;
  imageUrl?: string;
};

const ContractCard = ({
  id,
  project,
  name,
  client,
  location,
  value,
  measuredValue,
  status,
  imageUrl,
}: IContractCardProps) => {
  const classes = useStyles();
  const history = useHistory();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const statusChipType = {
    [EContractStatus.AGUARDANDO]: "info",
    [EContractStatus.ANDAMENTO]: "warning",
    [EContractStatus.PARALISADO]: "danger",
    [EContractStatus.CONCLUIDO]: "success",
    [EContractStatus.ARQUIVADO]: "default",
  };

  const handleOpenContract = () => {
    history.push(`/${id}/contract`);
  };

  const handleClickChangeImage = (event) => {
    event.stopPropagation();
    inputFileRef.current?.click();
  };

  const handleChangeInputFile = (event) => {
    const files = event.target.files;

    const contract = ContractListStore.findContract(id);
    if (!contract || !files[0]) return;

    ContractStore.uploadContractCover(contract, files[0]).catch((error) => {
      MessageStore.error(lang("kanban.updateCoverError", error?.message));
    });
  };

  return (
    <>
      <Card className={classes.root} onClick={handleOpenContract}>
        <CardMedia
          className={classes.media}
          image={imageUrl || defaultCover}
          title={project}
        >
          <ContractShareButton
            id={id}
            className={clsx("action-button", classes.cardButton)}
          />
          <IconButton
            className={clsx("action-button", classes.cardButton)}
            type="button"
            onClick={handleClickChangeImage}
          >
            <ImageIcon style={{ fontSize: "1rem" }} />
          </IconButton>
          <Typography className={classes.cardTitle}>{project}</Typography>
          <Typography className={classes.cardTitle}>
            {formatNumberToMonetary(value)}
          </Typography>
        </CardMedia>
        <CardContent>
          <Chip
            className={classes.chip}
            label={lang(`kanban.status.${status}`)}
            type={statusChipType[status]}
          />
          <Typography className={classes.cardText}>{name}</Typography>
          <Typography className={classes.cardText}>{client}</Typography>
          <Typography className={classes.cardText}>{location}</Typography>
          <ContractCardProgress value={measuredValue} />
        </CardContent>
      </Card>
      <input
        ref={inputFileRef}
        type="file"
        accept=".jpg, .jpeg, .png"
        style={{ display: "none" }}
        onChange={handleChangeInputFile}
      ></input>
    </>
  );
};

ContractCard.propTypes = {
  id: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  client: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  measuredValue: PropTypes.number.isRequired,
};

export default ContractCard;
